import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Grid, Typography, useEventCallback } from '@mui/material';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { memo, useEffect } from 'react';
import { TableNewFamilySubjectsVM } from 'practice/view-models/practice-edit-residence';
import { FormikProps } from 'formik';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { HttpResponseList } from 'shell/models/http-response';
import { PracticeFamily } from 'shell/models/practice-family';
import { FamilyVM } from '../../../step-family/family/family-vm';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import AccordionNewReidenceForm from '../../accordion-residence/accordion-actual-residence/form';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import TableNewFamily from './table-new-family';
import { transformFamilyTable } from 'practice/utils/transform-family-table';
import { FamilyModalVM } from '..';
import { FormActualResidenceVM } from 'practice/view-models/pratice-actual-residence';
import { ItalianVM } from 'shell/view-models/register-office-practice-vm';

interface AccordionFamilySubjectProps {
  formContext: FormikProps<FamilyModalVM>;
  open: boolean;
  setOpen: (value: boolean) => void;
  praticaKey?: {
    cdEnte: string;
    tipoPratica: string;
    idAnnoPratica: number;
    pgPratica: number;
  } | null;
}

const AccordionNewFamilySubjectsComponent = ({
  formContext,
  open,
  setOpen,
  praticaKey,
}: AccordionFamilySubjectProps): React.ReactElement => {
  const getFamilyMembers = useHttpClient<HttpResponseList<PracticeFamily>, Array<FamilyVM>>(
    `${__PRACTICE_API_URL__}/visualizzazione-scelta-famiglia`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          key: {
            tipoRegistro: 'IAIRE',
            idAnnoPratica: praticaKey?.idAnnoPratica,
            progressivoPratica: praticaKey?.pgPratica,
          },
        },
      },
      transformer: (response) => {
        return (
          transformFamilyTable(response.data.payload?.list)?.filter(
            (item) => item.selectedFromDb // solamente persone selezionate
          ) || []
        );
      },
    }
  );

  const changeResidenceData = useEventCallback(
    (
      key: keyof FormActualResidenceVM,
      value: FormActualResidenceVM[keyof FormActualResidenceVM]
    ) => {
      formContext.setFieldValue('tableNewFamilySubjects', {
        ...(formContext.values.tableNewFamilySubjects || {}),
        residence: {
          ...formContext.values.tableNewFamilySubjects?.residence,
          [key]: value,
        },
      });
    }
  );

  const onChange = useEventCallback(
    (
      key: keyof TableNewFamilySubjectsVM,
      value: TableNewFamilySubjectsVM[keyof TableNewFamilySubjectsVM]
    ) => {
      formContext.setFieldValue('tableNewFamilySubjects', {
        ...(formContext.values.tableNewFamilySubjects || {}),
        [key]: value,
      });
    }
  );

  useEffect(() => {
    (
      getFamilyMembers({
        data: {
          key: {
            tipoRegistro: 'IAIRE',
            idAnnoPratica: praticaKey?.idAnnoPratica,
            progressivoPratica: praticaKey?.pgPratica,
          },
        },
      }) as ResponsePromise<HttpResponseList<PracticeFamily>, Array<FamilyVM>>
    ).then((response) => {
      const castedResponse = response as Array<FamilyVM>;
      onChange(
        'body',
        castedResponse.map((item) => ({
          fiscalCode: item.fiscalCode || '',
          surname: item.surname || '',
          name: item.name || '',
          gender: item.generality?.gender || '',
          birthDate: item.birthDate || '',
          birthplace: item.birthplace || '',
          residence: item.residence || '',
          relationshipValue: item.relationship || '',
          subject: (item.subject as PracticeFamily) || undefined,
          posizionePersona: item.posizionePersona,
        }))
      );
    });
  }, []);

  const onChangeTableNewFamily = useEventCallback((newTable: TableNewFamilySubjectsVM) => {
    formContext.setFieldValue('tableNewFamilySubjects', newTable);
  });

  return (
    <Accordion expanded={open} onChange={() => setOpen(!open)}>
      <AccordionSummary
        aria-controls="panel-AccordionNewFamilySubjects-content"
        id="panel-AccordionNewFamilySubjects-content"
        expandIcon={<ExpandMore />}
      >
        <Typography>Elenco soggetti nuova famiglia</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {formContext.values.tableNewFamilySubjects?.residence ? (
            <>
              <Grid item xs={12}>
                <PmiTitleDivider>Residenza selezionata</PmiTitleDivider>
              </Grid>
              <Grid item xs={12}>
                <AccordionNewReidenceForm
                  values={formContext.values.tableNewFamilySubjects?.residence}
                  disabled={
                    !(formContext.values.tableNewFamilySubjects?.residence?.italian as ItalianVM)
                      ?.isCadastralObjectEmpty
                  }
                  onChange={changeResidenceData}
                />
              </Grid>
            </>
          ) : undefined}
          <TableNewFamily
            tableNewFamilySubjects={formContext.values.tableNewFamilySubjects}
            onChangeTableNewFamily={onChangeTableNewFamily}
          />
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

AccordionNewFamilySubjectsComponent.displayName = 'Accordion New Family Subjects component';

const AccordionNewFamilySubjects = memo(AccordionNewFamilySubjectsComponent);

export default AccordionNewFamilySubjects;
